// Ideas Service for backend communication
import { api } from './api';

export type IdeaType = 'Text' | 'Voice' | 'Image' | 'Link';
export type IdeaStatus = 'Inbox' | 'Promoted' | 'Archived';

export interface IdeaDto {
  id: string;
  userId: string;
  type: IdeaType;
  textContent?: string;
  contentUrl?: string;
  status: IdeaStatus;
  promotedToProjectId?: string;
  tags: string[];
  createdAt: string;
  updatedAt?: string;
}

export interface CreateIdeaRequest {
  type: string;
  textContent?: string;
  contentUrl?: string;
  tags?: string[];
}

export interface UpdateIdeaRequest {
  textContent?: string;
}

export interface AddTagRequest {
  tag: string;
}

export interface PromoteToProjectRequest {
  projectId: string;
}

// Ideas API
export const ideasApi = {
  // Get ideas in inbox
  getInbox: (): Promise<IdeaDto[]> => {
    return api.get<IdeaDto[]>('/ideas/inbox');
  },

  // Get all ideas with optional filters
  getIdeas: (status?: string, tag?: string): Promise<IdeaDto[]> => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (tag) params.append('tag', tag);
    const queryString = params.toString();
    return api.get<IdeaDto[]>(`/ideas${queryString ? `?${queryString}` : ''}`);
  },

  // Search ideas
  searchIdeas: (query: string): Promise<IdeaDto[]> => {
    return api.get<IdeaDto[]>(`/ideas/search?q=${encodeURIComponent(query)}`);
  },

  // Get a specific idea by ID
  getById: (id: string): Promise<IdeaDto> => {
    return api.get<IdeaDto>(`/ideas/${id}`);
  },

  // Create a new idea
  create: (data: CreateIdeaRequest): Promise<IdeaDto> => {
    return api.post<IdeaDto>('/ideas', data);
  },

  // Update an idea
  update: (id: string, data: UpdateIdeaRequest): Promise<IdeaDto> => {
    return api.put<IdeaDto>(`/ideas/${id}`, data);
  },

  // Delete an idea
  delete: (id: string): Promise<void> => {
    return api.delete(`/ideas/${id}`);
  },

  // Add a tag to an idea
  addTag: (id: string, tag: string): Promise<IdeaDto> => {
    return api.post<IdeaDto>(`/ideas/${id}/tags`, { tag });
  },

  // Remove a tag from an idea
  removeTag: (id: string, tag: string): Promise<IdeaDto> => {
    return api.delete<IdeaDto>(`/ideas/${id}/tags/${encodeURIComponent(tag)}`);
  },

  // Promote an idea to a project
  promoteToProject: (id: string, projectId: string): Promise<IdeaDto> => {
    return api.post<IdeaDto>(`/ideas/${id}/promote`, { projectId });
  },

  // Archive an idea
  archive: (id: string): Promise<IdeaDto> => {
    return api.post<IdeaDto>(`/ideas/${id}/archive`);
  },

  // Move an idea back to inbox
  moveToInbox: (id: string): Promise<IdeaDto> => {
    return api.post<IdeaDto>(`/ideas/${id}/inbox`);
  },
};

export default ideasApi;
